import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../theme';
import ImageUpload from '../components/ImageUpload';
import SortDropdown, { applySort } from '../components/SortDropdown';
import RaiseDisputeModal from '../components/RaiseDisputeModal';
import { auctions } from '../services/api';
import { PageLoader, EmptyState } from '../components/Loading';
import toast from 'react-hot-toast';

const inputStyle = (C) => ({
  width:'100%', padding:'10px 12px', background:C.input, border:`1px solid ${C.border}`,
  borderRadius:10, color:C.text, fontSize:13, outline:'none', boxSizing:'border-box', marginBottom:10
});

const TYPE_INFO = {
  english:      { icon:'🎯', label:'English',      desc:'Ascending bids' },
  sealed:       { icon:'🤐', label:'Sealed',       desc:'One blind bid each' },
  dutch:        { icon:'⏬', label:'Dutch',         desc:'Price drops over time' },
  reserve:      { icon:'💎', label:'Reserve',      desc:'Hidden minimum' },
  buy_now_only: { icon:'⚡', label:'Buy Now',      desc:'Instant purchase' },
};

const STATUS_COLOR = {
  draft:'#64748b', scheduled:'#0891b2', live:'#10b981',
  ended:'#f59e0b', paid:'#7c3aed', shipped:'#0891b2',
  completed:'#10b981', void:'#ef4444', cancelled:'#ef4444',
};

function timeRemaining(endAt) {
  const ms = new Date(endAt) - new Date();
  if (ms <= 0) return 'ended';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m left`;
  if (mins < 1440) return `${Math.floor(mins/60)}h left`;
  return `${Math.floor(mins/1440)}d left`;
}

export default function AuctionsScreen() {
  const C = useTheme();
  const nav = useNavigate();
  const [tab, setTab] = useState('my-auctions');
  const [items, setItems] = useState([]);
  const [myAuctions, setMyAuctions] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [bidsReceived, setBidsReceived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingAuction, setEditingAuction] = useState(null);
  const [detail, setDetail] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [sortKey, setSortKey] = useState('newest');

  const load = () => {
    setLoading(true);
    if (tab === 'browse') {
      auctions.browse(typeFilter ? {auction_type:typeFilter} : null).then(r => setItems(r.auctions || [])).catch(()=>{}).finally(()=>setLoading(false));
    } else if (tab === 'my-auctions') {
      auctions.myAuctions().then(r => setMyAuctions(r.auctions || [])).catch(()=>{}).finally(()=>setLoading(false));
    } else if (tab === 'bids-received') {
      auctions.bidsReceived().then(r => setBidsReceived(r.auctions || [])).catch(()=>{}).finally(()=>setLoading(false));
    } else {
      auctions.myBids().then(r => setMyBids(r.bids || [])).catch(()=>{}).finally(()=>setLoading(false));
    }
  };

  useEffect(() => { load(); }, [tab, typeFilter]);

  return (
    <div style={{background:C.bg,minHeight:'100vh',paddingBottom:80}}>
      <div style={{background:C.card,padding:'16px',borderBottom:`1px solid ${C.border}`,position:'sticky',top:0,zIndex:100}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
          <button onClick={()=>nav(-1)} style={{background:C.input,border:`1px solid ${C.border}`,borderRadius:10,padding:'6px 12px',color:C.text,fontSize:14,cursor:'pointer'}}>←</button>
          <div style={{flex:1}}>
            <div style={{fontSize:16,fontWeight:800,color:C.text}}>🎯 Auction House</div>
            <div style={{fontSize:11,color:C.muted}}>Bid · Win · Sell items</div>
          </div>
          <button onClick={()=>setShowCreate(true)} style={{background:C.primary,border:'none',borderRadius:10,padding:'6px 12px',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer'}}>+ List Item</button>
        </div>

        <div style={{display:'flex',gap:6}}>
          <button onClick={()=>setTab('my-auctions')} style={tabStyle(C, tab==='my-auctions')}>🏪 Selling</button>
          <button onClick={()=>setTab('bids-received')} style={tabStyle(C, tab==='bids-received')}>📥 Bids</button>

          <button onClick={()=>setTab('my-bids')} style={tabStyle(C, tab==='my-bids')}>💰 My Bids</button>
        </div>
      </div>

      <div style={{padding:16}}>
        {tab === 'browse' && (
          /* AUCTION SORT */
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:8}}>
            <SortDropdown C={C} value={sortKey} onChange={setSortKey} options={[
              {value:'newest',label:'Newest first'},
              {value:'ending_soon',label:'Ending soon'},
              {value:'price_low',label:'Price: low to high'},
              {value:'price_high',label:'Price: high to low'},
            ]} />
          </div>
        )}
        {tab === 'browse' && (
          <div style={{display:'flex',gap:6,overflowX:'auto',marginBottom:14}}>
            {['', 'english','sealed','dutch','reserve','buy_now_only'].map(t => (
              <button key={t||'all'} onClick={()=>setTypeFilter(t)} style={{
                background: typeFilter===t ? C.primary : C.input,
                color: typeFilter===t ? '#fff' : C.text,
                border:`1px solid ${typeFilter===t?C.primary:C.border}`,
                borderRadius:8, padding:'4px 10px', fontSize:10, fontWeight:600,
                whiteSpace:'nowrap', cursor:'pointer', flexShrink:0,
              }}>
                {t ? `${TYPE_INFO[t].icon} ${TYPE_INFO[t].label}` : 'All'}
              </button>
            ))}
          </div>
        )}

        {loading ? <PageLoader C={C} /> : (
          <>
            {tab === 'browse' && (items.length === 0 ?
              <EmptyState C={C} icon="🎯" title="No live auctions" message="Be the first to list an item!" /> :
              applySort(items, sortKey).map(a => <AuctionCard key={a.id} a={a} C={C} onClick={()=>setDetail(a.id)} />)
            )}

            {tab === 'bids-received' && (bidsReceived.length === 0 ?
              <EmptyState C={C} icon="📥" title="No auction listings" message="List an auction to start receiving bids" /> :
              bidsReceived.map(a => (
                <div key={a.auction_id} onClick={()=>setDetail(a.auction_id)} style={{
                  background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:12,
                  marginBottom:10, cursor:'pointer', boxShadow:C.shadow,
                }}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:800,color:C.text}}>{a.title}</div>
                      <div style={{fontSize:10,color:C.muted,marginTop:2}}>{a.auction_type} · {a.status}</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:14,fontWeight:800,color: a.total_bids > 0 ? '#10b981' : C.muted}}>
                        {a.total_bids} bid{a.total_bids !== 1 ? 's' : ''}
                      </div>
                      <div style={{fontSize:10,color:C.muted}}>{a.unique_bidders} bidder{a.unique_bidders !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:C.muted,marginTop:8,paddingTop:8,borderTop:`1px solid ${C.border}`}}>
                    <span>Current: <strong style={{color:C.text}}>{a.current_price} TXC</strong></span>
                    {a.highest_bid != null && <span>Highest: <strong style={{color:'#10b981'}}>{a.highest_bid} TXC</strong></span>}
                  </div>
                </div>
              ))
            )}
            {tab === 'my-auctions' && (myAuctions.length === 0 ?
              <EmptyState C={C} icon="🏪" title="No auctions yet" message="List your first item to sell" /> :
              myAuctions.map(a => <AuctionCard key={a.id} a={a} C={C} onClick={()=>setDetail(a.id)} showStatus />)
            )}

            {tab === 'my-bids' && (myBids.length === 0 ?
              <EmptyState C={C} icon="💰" title="No bids yet" message="Browse and place your first bid" /> :
              myBids.map(b => (
                <div key={b.auction_id} onClick={()=>setDetail(b.auction_id)} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:12,marginBottom:8,cursor:'pointer'}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{b.title}</div>
                    <div style={{fontSize:9,padding:'2px 6px',borderRadius:4,fontWeight:700,
                      background: b.is_won?'#10b98118':b.is_winning?'#0891b218':'#64748b18',
                      color: b.is_won?'#10b981':b.is_winning?'#0891b2':'#64748b'}}>
                      {b.is_won ? '🏆 WON' : b.is_winning ? '⭐ LEADING' : 'OUTBID'}
                    </div>
                  </div>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:11}}>
                    <span style={{color:C.muted}}>My bid: <strong style={{color:C.text}}>{b.my_high_bid} TXC</strong></span>
                    <span style={{color:C.muted}}>{b.status === 'live' ? timeRemaining(b.end_at) : b.status.toUpperCase()}</span>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>

      {(showCreate || editingAuction) && (
        <CreateAuctionForm
          C={C}
          editingAuction={editingAuction}
          onClose={()=>{setShowCreate(false); setEditingAuction(null);}}
          onSuccess={()=>{setShowCreate(false); setEditingAuction(null); load();}}
        />
      )}
      {detail && <AuctionDetail C={C} id={detail} onClose={()=>{setDetail(null); load();}} />}
    </div>
  );
}

function tabStyle(C, active) {
  return {
    flex:1, background:active?C.primary:C.input, color:active?'#fff':C.text,
    border:'none', borderRadius:10, padding:'8px', fontSize:11, fontWeight:700, cursor:'pointer',
  };
}

function AuctionCard({ a, C, onClick, showStatus, onEdit }) {
  const ti = TYPE_INFO[a.auction_type] || {icon:'🎯',label:a.auction_type};
  return (
    <div onClick={onClick} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:14,marginBottom:10,cursor:'pointer'}}>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:800,color:C.text}}>{a.title}</div>
          <div style={{fontSize:10,color:C.muted}}>by @{a.seller_name} {a.category && `· ${a.category}`}</div>
        </div>
        {showStatus ? (
          <div style={{fontSize:9,padding:'3px 8px',borderRadius:6,fontWeight:700,background:(STATUS_COLOR[a.status]||C.muted)+'18',color:STATUS_COLOR[a.status]||C.muted}}>
            {a.status.toUpperCase()}
          </div>
        ) : (
          <div style={{fontSize:9,padding:'3px 8px',borderRadius:6,fontWeight:700,background:'#7c3aed18',color:'#7c3aed'}}>
            {ti.icon} {ti.label.toUpperCase()}
          </div>
        )}
      </div>

      <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
        <div>
          <div style={{fontSize:9,color:C.muted}}>Current</div>
          <div style={{fontSize:16,fontWeight:800,color:C.primary}}>{a.current_price} TXC</div>
        </div>
        {a.buy_now_price && (
          <div style={{textAlign:'right'}}>
            <div style={{fontSize:9,color:C.muted}}>Buy Now</div>
            <div style={{fontSize:14,fontWeight:700,color:'#10b981'}}>⚡ {a.buy_now_price}</div>
          </div>
        )}
      </div>

      <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:C.muted}}>
        <span>👥 {a.total_bidders} bidders · 📊 {a.total_bids} bids</span>
        <span style={{color:a.status==='live'?'#10b981':C.muted,fontWeight:700}}>
          {a.status === 'live' ? '🟢 ' + timeRemaining(a.end_at) : a.status.toUpperCase()}
        </span>
      </div>
    
      {onEdit && (
        <button onClick={(e)=>{e.stopPropagation(); onEdit();}} style={{
          width:'100%', marginTop:8, background:'transparent', border:'1px solid '+C.border,
          borderRadius:8, padding:'7px', color:C.text, fontSize:11, fontWeight:700, cursor:'pointer',
        }}>✏️ Edit Auction</button>
      )}
      </div>
  );
}

function CreateAuctionForm({ C, onClose, onSuccess, editingAuction }) {
  const [form, setForm] = useState(() => editingAuction ? {
    title: editingAuction.title || '',
    description: editingAuction.description || '',
    auction_type: editingAuction.auction_type || 'english',
    starting_price: editingAuction.starting_price != null ? String(editingAuction.starting_price) : '',
    reserve_price: editingAuction.reserve_price != null ? String(editingAuction.reserve_price) : '',
    buy_now_price: editingAuction.buy_now_price != null ? String(editingAuction.buy_now_price) : '',
    bid_increment: editingAuction.bid_increment != null ? String(editingAuction.bid_increment) : '50',
    deposit_pct: editingAuction.deposit_pct != null ? String(editingAuction.deposit_pct) : '10',
    category: editingAuction.category || '',
    condition: editingAuction.condition || 'used',
    duration_hours: '72',
    images: Array.isArray(editingAuction.images) ? editingAuction.images : [],
  } : {
    title:'', description:'', auction_type:'english',
    starting_price:'', reserve_price:'', buy_now_price:'',
    bid_increment:'50', deposit_pct:'10', duration_hours:'24',
    category:'', condition:'used',
    dutch_drop_amount:'', dutch_drop_interval_minutes:'30', dutch_floor_price:'',
    images:[],
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (publish) => {
    if (!form.title || !form.description || !form.starting_price) {
      toast.error('Title, description, starting price required');
      return;
    }
    setSubmitting(true);
    try {
      const images = form.images || [];
      const payload = {
        title: form.title, description: form.description,
        auction_type: form.auction_type,
        starting_price: parseFloat(form.starting_price),
        bid_increment: parseFloat(form.bid_increment) || 50,
        deposit_pct: parseFloat(form.deposit_pct) || 10,
        duration_hours: parseInt(form.duration_hours) || 24,
        category: form.category, condition: form.condition,
        images, publish,
      };
      if (form.reserve_price) payload.reserve_price = parseFloat(form.reserve_price);
      if (form.buy_now_price) payload.buy_now_price = parseFloat(form.buy_now_price);
      if (form.auction_type === 'dutch') {
        payload.dutch_drop_amount = parseFloat(form.dutch_drop_amount) || 0;
        payload.dutch_drop_interval_minutes = parseInt(form.dutch_drop_interval_minutes) || 30;
        payload.dutch_floor_price = parseFloat(form.dutch_floor_price) || 0;
      }
      if (editingAuction) {
        await auctions.update(editingAuction.id, payload);
        toast.success('Updated!');
      } else {
        await auctions.create(payload);
        toast.success('Listed!');
      }
      onSuccess();
    } catch(e) { toast.error(e.message); setSubmitting(false); }
  };

  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:10000,display:'flex',alignItems:'flex-end',justifyContent:'center'}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.bg,borderRadius:'24px 24px 0 0',width:'100%',maxWidth:430,maxHeight:'92vh',overflowY:'auto',padding:16}}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:14}}>
          <div style={{fontSize:16,fontWeight:800,color:C.text}}>{editingAuction ? 'Edit Auction' : 'List Auction'}</div>
          <button onClick={onClose} style={{background:C.input,border:`1px solid ${C.border}`,borderRadius:'50%',width:30,height:30,fontSize:14,cursor:'pointer',color:C.muted}}>×</button>
        </div>

        {/* ▔▔▔ SECTION: Auction type cards ▒▒⒒ */}
        <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:0.8,margin:'2px 0 8px'}}>Auction type</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(95px, 1fr))',gap:6,marginBottom:12}}>
          {Object.entries(TYPE_INFO).map(([k, v]) => {
            const active = form.auction_type === k;
            return (
              <button key={k} onClick={() => setForm({...form, auction_type: k})} style={{
                background: active ? `${C.primary}18` : C.input,
                border: `2px solid ${active ? C.primary : C.border}`,
                borderRadius: 12,
                padding: '8px 4px',
                cursor: 'pointer',
                textAlign: 'center',
              }}>
                <div style={{fontSize: 18}}>{v.icon}</div>
                <div style={{fontSize:10,fontWeight:800,color: active ? C.primary : C.text,margin:'3px 0 1px'}}>{v.label}</div>
                <div style={{fontSize:8,color:C.muted,lineHeight:1.2}}>{v.desc}</div>
              </button>
            );
          })}
        </div>

        {/* ▔▔▔ SECTION: Item details ▒⒒⒒ */}
        <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:0.8,margin:'4px 0 8px'}}>Item details</div>
        <input placeholder="Title (e.g. MacBook Pro 16-inch) *" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} style={inputStyle(C)} />
        <textarea placeholder="Describe condition, what's included, any quirks *" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} style={{...inputStyle(C),minHeight:80,resize:'vertical'}} />
        <ImageUpload C={C} value={form.images || []} onChange={imgs=>setForm({...form, images: imgs})} max={5} label="Auction Images" />
        <div style={{display:'flex',gap:8}}>
          <input placeholder="Category (e.g. electronics)" value={form.category} onChange={e=>setForm({...form,category:e.target.value})} style={{...inputStyle(C),flex:1}} />
          <select value={form.condition} onChange={e=>setForm({...form,condition:e.target.value})} style={{...inputStyle(C),flex:1}}>
            <option value="new">New</option>
            <option value="like_new">Like new</option>
            <option value="used">Used</option>
            <option value="refurbished">Refurbished</option>
            <option value="vintage">Vintage</option>
          </select>
        </div>

        {/* ▔▔▔ SECTION: Pricing ▒⒒⒒ */}
        <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:0.8,margin:'8px 0 8px'}}>Pricing (TXC)</div>
        <div style={{display:'flex',gap:8}}>
          <input type="number" placeholder={form.auction_type === 'buy_now_only' ? 'Price * TXC' : 'Starting price *'} value={form.starting_price} onChange={e=>setForm({...form,starting_price:e.target.value})} style={{...inputStyle(C),flex:1}} />
          {form.auction_type !== 'sealed' && form.auction_type !== 'dutch' && form.auction_type !== 'buy_now_only' && (
            <input type="number" placeholder="Buy-now (optional)" value={form.buy_now_price} onChange={e=>setForm({...form,buy_now_price:e.target.value})} style={{...inputStyle(C),flex:1}} />
          )}
        </div>

        {form.auction_type === 'reserve' && (
          <input type="number" placeholder="Reserve price (hidden minimum - won't sell below)" value={form.reserve_price} onChange={e=>setForm({...form,reserve_price:e.target.value})} style={inputStyle(C)} />
        )}

        {form.auction_type === 'dutch' && (
          <>
            <div style={{display:'flex',gap:8}}>
              <input type="number" placeholder="Drop amount/interval" value={form.dutch_drop_amount} onChange={e=>setForm({...form,dutch_drop_amount:e.target.value})} style={{...inputStyle(C),flex:1}} />
              <input type="number" placeholder="Interval (mins)" value={form.dutch_drop_interval_minutes} onChange={e=>setForm({...form,dutch_drop_interval_minutes:e.target.value})} style={{...inputStyle(C),flex:1}} />
            </div>
            <input type="number" placeholder="Floor price (won't drop below)" value={form.dutch_floor_price} onChange={e=>setForm({...form,dutch_floor_price:e.target.value})} style={inputStyle(C)} />
          </>
        )}

        {/* ▔▔▔ SECTION: Settings ▒⒒⒒ */}
        <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:0.8,margin:'8px 0 8px'}}>Auction settings</div>
        <div style={{display:'flex',gap:8}}>
          {form.auction_type !== 'dutch' && form.auction_type !== 'buy_now_only' && (
            <input type="number" placeholder="Bid increment" value={form.bid_increment} onChange={e=>setForm({...form,bid_increment:e.target.value})} style={{...inputStyle(C),flex:1}} />
          )}
          {form.auction_type !== 'buy_now_only' && (
            <input type="number" placeholder="Deposit %" value={form.deposit_pct} onChange={e=>setForm({...form,deposit_pct:e.target.value})} style={{...inputStyle(C),flex:1}} />
          )}
          <input type="number" placeholder="Duration (hrs)" value={form.duration_hours} onChange={e=>setForm({...form,duration_hours:e.target.value})} style={{...inputStyle(C),flex:1}} />
        </div>

        {/* ▔▔▔ Preview ▒⒒⒒ */}
        {form.starting_price && form.title && (
          <div style={{margin:'12px 0 4px',padding:'10px 12px',background:`${C.primary}0F`,borderRadius:10,border:`1px solid ${C.primary}33`,fontSize:11,color:C.text,lineHeight:1.5}}>
            <span style={{color:C.primary,fontWeight:800}}>Preview:</span> {TYPE_INFO[form.auction_type]?.label} auction, runs {form.duration_hours} hrs. Starting at {form.starting_price} TXC.{form.buy_now_price && form.auction_type !== 'sealed' && form.auction_type !== 'dutch' && form.auction_type !== 'buy_now_only' ? ` Buy-now ${form.buy_now_price} TXC.` : ''}{form.auction_type === 'reserve' && form.reserve_price ? ` Reserve (hidden) ${form.reserve_price} TXC.` : ''}
          </div>
        )}

        <div style={{display:'flex',gap:8,marginTop:6}}>
          <button onClick={()=>submit(false)} disabled={submitting} style={{flex:1,background:C.input,border:`1px solid ${C.border}`,borderRadius:12,padding:'12px',color:C.text,fontSize:13,fontWeight:700,cursor:'pointer'}}>Save Draft</button>
          <button onClick={()=>submit(true)} disabled={submitting} style={{flex:1.5,background:C.primary,border:'none',borderRadius:12,padding:'12px',color:'#fff',fontSize:13,fontWeight:800,cursor:submitting?'wait':'pointer'}}>
            {submitting ? '...' : 'Publish Live'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AuctionDetail({ C, id, onClose }) {
  const [data, setData] = useState(null);
  const [showDispute, setShowDispute] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showShip, setShowShip] = useState(false);
  const [tracking, setTracking] = useState('');

  const load = () => {
    setLoading(true);
    auctions.get(id).then(d => setData(d)).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(() => { load(); }, [id]);

  // Live timer for live auctions
  useEffect(() => {
    if (!data || data.status !== 'live') return;
    const t = setInterval(() => setData(d => d ? {...d, seconds_remaining: Math.max(0, d.seconds_remaining - 1)} : d), 1000);
    return () => clearInterval(t);
  }, [data?.status]);

  const payDeposit = async () => {
    setSubmitting(true);
    try {
      const r = await auctions.payDeposit(id);
      toast.success(`Deposit ${r.deposit_amount} TXC locked`);
      load();
    } catch(e) { toast.error(e.message); }
    setSubmitting(false);
  };

  const placeBid = async (isBuyNow) => {
    setSubmitting(true);
    try {
      const r = await auctions.bid(id, {
        amount: isBuyNow ? data.buy_now_price : parseFloat(bidAmount),
        is_buy_now: isBuyNow,
      });
      if (r.is_winner) toast.success('🏆 You won!');
      else toast.success('Bid placed!');
      setBidAmount('');
      load();
    } catch(e) { toast.error(e.message); }
    setSubmitting(false);
  };

  const payBalance = async () => {
    setSubmitting(true);
    try {
      const r = await auctions.payBalance(id);
      toast.success(`Paid ${r.balance_paid} TXC`);
      load();
    } catch(e) { toast.error(e.message); }
    setSubmitting(false);
  };

  const ship = async () => {
    if (!tracking) return toast.error('Tracking required');
    try {
      await auctions.ship(id, { tracking_number: tracking });
      toast.success('Marked as shipped');
      setShowShip(false); setTracking(''); load();
    } catch(e) { toast.error(e.message); }
  };

  const confirmDelivery = async () => {
    try { await auctions.confirmDelivery(id); toast.success('Delivery confirmed'); load(); }
    catch(e) { toast.error(e.message); }
  };

  if (loading || !data) return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'#fff'}}>Loading...</div>
    </div>
  );

  const ti = TYPE_INFO[data.auction_type];
  const minBid = data.current_price + data.bid_increment;
  const hasDeposit = data.my_deposit?.status === 'locked';
  const isWinner = data.winner_id && data.status !== 'live';
  const isMe = data.is_owner;

  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:10000,display:'flex',alignItems:'flex-end',justifyContent:'center'}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.bg,borderRadius:'24px 24px 0 0',width:'100%',maxWidth:430,maxHeight:'92vh',overflowY:'auto',padding:16}}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:14}}>
          <div style={{flex:1}}>
            <div style={{fontSize:16,fontWeight:800,color:C.text}}>{data.title}</div>
            <div style={{fontSize:10,color:C.muted}}>{ti.icon} {ti.label} · by @{data.seller_name}</div>
          </div>
          <button onClick={onClose} style={{background:C.input,border:`1px solid ${C.border}`,borderRadius:'50%',width:32,height:32,fontSize:16,cursor:'pointer',color:C.muted}}>×</button>
        </div>

        {data.images?.length > 0 && (
          <div style={{display:'flex',gap:6,overflowX:'auto',marginBottom:12}}>
            {data.images.map((url, i) => (
              <img key={i} src={url} alt="" style={{height:100,borderRadius:8,objectFit:'cover',flexShrink:0}} />
            ))}
          </div>
        )}

        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:14,marginBottom:12,textAlign:'center'}}>
          <div style={{fontSize:9,color:C.muted,fontWeight:700,marginBottom:4}}>CURRENT PRICE</div>
          <div style={{fontSize:32,fontWeight:800,color:C.primary}}>{data.current_price} TXC</div>
          {data.buy_now_price && (
            <div style={{fontSize:10,color:'#10b981',marginTop:4}}>⚡ Buy Now: {data.buy_now_price} TXC</div>
          )}
          {data.status === 'live' && (
            <div style={{fontSize:11,color:'#f59e0b',fontWeight:700,marginTop:6}}>
              ⏰ {Math.floor(data.seconds_remaining/60)}m {data.seconds_remaining%60}s remaining
            </div>
          )}
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6,marginBottom:12}}>
          <Stat C={C} label="Bidders" value={data.total_bidders} />
          <Stat C={C} label="Bids" value={data.total_bids} />
          <Stat C={C} label="Status" value={data.status} color={STATUS_COLOR[data.status]} />
        </div>

        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:12,marginBottom:12,fontSize:12,color:C.text,lineHeight:1.5,whiteSpace:'pre-wrap'}}>
          {data.description}
        </div>

        {data.condition && <div style={{fontSize:10,color:C.muted,marginBottom:12}}>Condition: <strong>{data.condition}</strong></div>}

        {/* BIDDING UI - non-owner, live */}
        {!isMe && data.status === 'live' && data.auction_type !== 'buy_now_only' && (
          <>
            {!hasDeposit && (
              <div style={{background:'#f59e0b18',border:'1px solid #f59e0b33',borderRadius:12,padding:12,marginBottom:10}}>
                <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:6}}>🔐 Pay deposit to bid</div>
                <div style={{fontSize:11,color:C.muted,marginBottom:8}}>{data.deposit_required} TXC will be held in escrow. Refunded if you don't win.</div>
                <button onClick={payDeposit} disabled={submitting} style={{width:'100%',background:C.primary,border:'none',borderRadius:10,padding:'10px',color:'#fff',fontSize:12,fontWeight:700,cursor:submitting?'wait':'pointer'}}>
                  Pay {data.deposit_required} TXC Deposit
                </button>
              </div>
            )}

            {hasDeposit && (
              <div style={{background:'#10b98118',border:'1px solid #10b98133',borderRadius:12,padding:12,marginBottom:10}}>
                <div style={{fontSize:11,color:'#10b981',fontWeight:700}}>✓ Deposit {data.my_deposit.amount} TXC locked. You can bid.</div>
              </div>
            )}

            {hasDeposit && data.auction_type === 'english' && (
              <div style={{display:'flex',gap:8,marginBottom:10}}>
                <input type="number" placeholder={`Min: ${minBid}`} value={bidAmount} onChange={e=>setBidAmount(e.target.value)} style={{...inputStyle(C),flex:1,marginBottom:0}} />
                <button onClick={()=>placeBid(false)} disabled={submitting||!bidAmount} style={{background:C.primary,border:'none',borderRadius:10,padding:'0 16px',color:'#fff',fontSize:12,fontWeight:700,cursor:submitting?'wait':'pointer'}}>Bid</button>
              </div>
            )}

            {hasDeposit && data.auction_type === 'sealed' && (
              <div style={{display:'flex',gap:8,marginBottom:10}}>
                <input type="number" placeholder="Your sealed bid (one-shot)" value={bidAmount} onChange={e=>setBidAmount(e.target.value)} style={{...inputStyle(C),flex:1,marginBottom:0}} />
                <button onClick={()=>placeBid(false)} disabled={submitting||!bidAmount} style={{background:'#7c3aed',border:'none',borderRadius:10,padding:'0 16px',color:'#fff',fontSize:12,fontWeight:700,cursor:submitting?'wait':'pointer'}}>🤐 Submit</button>
              </div>
            )}

            {hasDeposit && data.auction_type === 'dutch' && (
              <button onClick={()=>placeBid(false)} disabled={submitting} style={{width:'100%',background:'#7c3aed',border:'none',borderRadius:10,padding:'12px',color:'#fff',fontSize:13,fontWeight:800,cursor:submitting?'wait':'pointer',marginBottom:10}}>
                ⏬ Accept {data.current_price} TXC (Win Now)
              </button>
            )}

            {data.buy_now_price && (
              <button onClick={()=>placeBid(true)} disabled={submitting} style={{width:'100%',background:`linear-gradient(135deg, #10b981, ${C.primary})`,border:'none',borderRadius:10,padding:'12px',color:'#fff',fontSize:13,fontWeight:800,cursor:submitting?'wait':'pointer',marginBottom:10}}>
                ⚡ Buy Now {data.buy_now_price} TXC
              </button>
            )}
          </>
        )}

        {/* Buy Now only auction */}
        {!isMe && data.status === 'live' && data.auction_type === 'buy_now_only' && data.buy_now_price && (
          <button onClick={()=>placeBid(true)} disabled={submitting} style={{width:'100%',background:`linear-gradient(135deg, #10b981, ${C.primary})`,border:'none',borderRadius:10,padding:'14px',color:'#fff',fontSize:14,fontWeight:800,cursor:submitting?'wait':'pointer',marginBottom:10}}>
            ⚡ Buy Now {data.buy_now_price} TXC
          </button>
        )}

        {/* WINNER ACTIONS - with 24h deadline */}
        {data.winner_id && data.status === 'ended' && !isMe && data.winner_id === window.__currentUserId && (
          <PaymentDeadline C={C} updatedAt={data.updated_at} />
        )}
        {data.winner_id && data.status === 'ended' && !isMe && (
          <button onClick={payBalance} disabled={submitting} style={{width:'100%',background:'#10b981',border:'none',borderRadius:10,padding:'14px',color:'#fff',fontSize:14,fontWeight:800,cursor:submitting?'wait':'pointer',marginBottom:10}}>
            💳 Pay {data.final_price} TXC to Complete
          </button>
        )}

        {/* SELLER ACTIONS */}
        {isMe && data.status === 'paid' && (
          <button onClick={()=>setShowShip(true)} style={{width:'100%',background:C.primary,border:'none',borderRadius:10,padding:'14px',color:'#fff',fontSize:14,fontWeight:800,cursor:'pointer',marginBottom:10}}>
            📦 Mark as Shipped
          </button>
        )}

        {/* WINNER CONFIRM DELIVERY */}
        {data.winner_id && data.status === 'shipped' && !isMe && (
          <button onClick={confirmDelivery} style={{width:'100%',background:'#10b981',border:'none',borderRadius:10,padding:'14px',color:'#fff',fontSize:14,fontWeight:800,cursor:'pointer',marginBottom:10}}>
            ✓ Confirm Delivery Received
          </button>
        )}

        {data.tracking_number && (
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:10,fontSize:11,color:C.muted,marginBottom:10}}>
            📦 Tracking: <strong style={{color:C.text}}>{data.tracking_number}</strong>
          </div>
        )}

        {/* BID HISTORY */}
        {data.bids?.length > 0 && (
          <div style={{marginTop:12}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:700,marginBottom:8,textTransform:'uppercase'}}>📊 Bid History</div>
            {data.bids.slice(0,10).map(b => (
              <div key={b.id} style={{display:'flex',justifyContent:'space-between',padding:'8px 10px',background:C.card,border:`1px solid ${b.is_mine?C.primary:C.border}`,borderRadius:8,marginBottom:4}}>
                <span style={{fontSize:11,color:C.text}}>{b.is_mine && '★ '}{b.bidder_name}{b.is_buy_now && ' ⚡'}</span>
                <span style={{fontSize:11,fontWeight:700,color:b.is_winning?'#10b981':C.text}}>{b.amount} TXC{b.is_winning && ' 👑'}</span>
              </div>
            ))}
          </div>
        )}

        {showShip && (
          <div onClick={()=>setShowShip(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:10001,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
            <div onClick={e=>e.stopPropagation()} style={{background:C.bg,borderRadius:14,padding:16,maxWidth:380,width:'100%'}}>
              <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:12}}>Mark as Shipped</div>
              <input placeholder="Tracking number" value={tracking} onChange={e=>setTracking(e.target.value)} style={inputStyle(C)} />
              <div style={{display:'flex',gap:8}}>
                <button onClick={()=>setShowShip(false)} style={{flex:1,background:C.input,border:`1px solid ${C.border}`,borderRadius:10,padding:'10px',color:C.text,fontSize:12,cursor:'pointer'}}>Cancel</button>
                <button onClick={ship} style={{flex:1.5,background:C.primary,border:'none',borderRadius:10,padding:'10px',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer'}}>Confirm Ship</button>
              </div>
            </div>
          </div>
        )}
        {(data?.status === 'ended' || data?.winner_id) && (
          <button onClick={()=>setShowDispute(true)} style={{
            width:'100%', marginTop:10, background:'#ef444418', border:'1px solid #ef444433',
            borderRadius:10, padding:'10px', color:'#ef4444', fontSize:12, fontWeight:700, cursor:'pointer',
          }}>⚖️ Raise Dispute</button>
        )}
        {showDispute && (
          <RaiseDisputeModal C={C} targetType="auction_order" targetId={id}
            onClose={()=>setShowDispute(false)} onSuccess={()=>{setShowDispute(false); onClose();}} />
        )}
      </div>
    </div>
  );
}

function Stat({ C, label, value, color }) {
  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:'8px',textAlign:'center'}}>
      <div style={{fontSize:14,fontWeight:800,color:color||C.text}}>{value}</div>
      <div style={{fontSize:9,color:C.muted}}>{label}</div>
    </div>
  );
}

function PaymentDeadline({ C, updatedAt }) {
  const [secondsLeft, setSecondsLeft] = React.useState(0);
  
  React.useEffect(() => {
    const calc = () => {
      const deadline = new Date(updatedAt).getTime() + 24 * 60 * 60 * 1000;
      return Math.max(0, Math.floor((deadline - Date.now()) / 1000));
    };
    setSecondsLeft(calc());
    const t = setInterval(() => setSecondsLeft(calc()), 1000);
    return () => clearInterval(t);
  }, [updatedAt]);
  
  if (secondsLeft <= 0) return (
    <div style={{background:'#ef444418',border:'1px solid #ef444433',borderRadius:10,padding:10,marginBottom:10,textAlign:'center'}}>
      <div style={{fontSize:12,fontWeight:700,color:'#ef4444'}}>⚠️ Payment deadline expired</div>
      <div style={{fontSize:10,color:C.muted,marginTop:2}}>Your deposit may be forfeited soon</div>
    </div>
  );
  
  const hours = Math.floor(secondsLeft / 3600);
  const mins = Math.floor((secondsLeft % 3600) / 60);
  const secs = secondsLeft % 60;
  const isUrgent = secondsLeft < 3600;
  
  return (
    <div style={{
      background: isUrgent ? '#ef444418' : '#f59e0b18',
      border: `1px solid ${isUrgent ? '#ef444433' : '#f59e0b33'}`,
      borderRadius:10, padding:10, marginBottom:10, textAlign:'center'
    }}>
      <div style={{fontSize:10,color:C.muted,fontWeight:700,marginBottom:2}}>⏱ PAYMENT DEADLINE</div>
      <div style={{fontSize:18,fontWeight:800,color:isUrgent?'#ef4444':'#f59e0b'}}>
        {hours}h {mins}m {secs}s
      </div>
      <div style={{fontSize:10,color:C.muted,marginTop:2}}>Pay before timer expires or forfeit deposit</div>
    </div>
  );
}

